import { z } from 'zod';
import { geofencePolygonProblem } from './geofence-polygon';
import { defineListQuery, listFilter } from './list-query.schemas';
import { zoneGeoJsonPolygonSchema, type ZoneGeoJsonPolygon } from './pricing.schemas';

/**
 * Flagged areas (gitaumoses4/chukua#96): places the office marks on a map so the unsafe-stop alert knows where a stop matters. `unsafe` is
 * somewhere a stop is dangerous at any hour; `safe_stop` is a normal rest place, where an overnight stop is expected and not flagged.
 * Our own data, not a geofence: nothing is pushed to a GPS vendor and nothing records a crossing.
 */
export const FLAGGED_AREA_SAFETIES = ['unsafe', 'safe_stop'] as const;
export type FlaggedAreaSafety = (typeof FLAGGED_AREA_SAFETIES)[number];

export const flaggedAreaInputSchema = z
  .object({
    name: z.string().trim().min(1).max(160),
    safety: z.enum(FLAGGED_AREA_SAFETIES),
    note: z.string().trim().max(500).nullable().optional(),
    polygon: zoneGeoJsonPolygonSchema,
  })
  .strict()
  .superRefine((area, ctx) => {
    const problem = geofencePolygonProblem(area.polygon);
    if (problem) ctx.addIssue({ code: 'custom', path: ['polygon'], message: problem });
  });
export type FlaggedAreaInput = z.infer<typeof flaggedAreaInputSchema>;

const isActiveFilter = z
  .enum(['true', 'false'])
  .transform((value) => value === 'true')
  .optional();

/** `GET /flagged-areas`: search matches the name; `isActive` is `true`/`false` text, never coerced (CLAUDE.md → Lists and tables). */
export const flaggedAreaListQuerySchema = defineListQuery({
  sortFields: ['name', 'safety', 'updatedAt'],
  defaultSort: { sort: 'name', order: 'asc' },
  filters: { safety: listFilter(z.enum(FLAGGED_AREA_SAFETIES)), isActive: isActiveFilter },
});
export type FlaggedAreaListQuery = z.infer<typeof flaggedAreaListQuerySchema>;

/** `GET /flagged-areas/map`: every area the list's search and filters match, unpaged, because a map showing one page would be wrong. */
export const flaggedAreaMapQuerySchema = flaggedAreaListQuerySchema.pick({ q: true, safety: true, isActive: true });
export type FlaggedAreaMapQuery = z.infer<typeof flaggedAreaMapQuerySchema>;

export interface FlaggedArea {
  id: string;
  name: string;
  safety: FlaggedAreaSafety;
  note: string | null;
  polygon: ZoneGeoJsonPolygon;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}
